import { Controller } from "@hotwired/stimulus"

// The Area, Subdivision and Beds selects on the request forms, and the Area
// and Subdivision selects in the desk's "Change location". The form carries
// the whole tree in one data attribute (ProjectionRequestsHelper#subdivision_tree):
// { areaId: [{ id, name, beds: [3, 4] }] }, so nothing public is queried.
// Subdivision lists the picked area's subdivisions, then "Not on the list",
// which shows the name box (notListed). Beds, where there is one, lists the
// counts with season data first and every other size after, so no home size
// is refused. Without the script the server's options still work.
const NOT_LISTED = "not_listed"

export default class extends Controller {
  static targets = ["area", "subdivision", "bedrooms", "notListed"]
  static values = { tree: Object, maxBedrooms: Number }

  connect() {
    this.areaChanged()
  }

  areaChanged() {
    this.fillSubdivisions()
    this.subdivisionChanged()
  }

  subdivisionChanged() {
    this.toggleNotListed()
    if (this.hasBedroomsTarget) this.fillBedrooms()
  }

  fillSubdivisions() {
    const select = this.subdivisionTarget
    const chosen = select.value
    const list = this.subdivisionsOfArea()
    select.replaceChildren(
      this.blankOption(select),
      ...list.map(({ id, name }) => new Option(name, id)),
      this.notListedOption(select)
    )
    const stillListed = chosen === NOT_LISTED || list.some(({ id }) => String(id) === chosen)
    select.value = stillListed ? chosen : ""
  }

  // The name box shows, and posts, only while "Not on the list" is picked.
  toggleNotListed() {
    if (!this.hasNotListedTarget) return

    const shown = this.subdivisionTarget.value === NOT_LISTED
    this.notListedTarget.hidden = !shown
    this.notListedTarget.querySelectorAll("input").forEach((input) => { input.disabled = !shown })
  }

  fillBedrooms() {
    const select = this.bedroomsTarget
    const chosen = select.value
    const picked = this.subdivisionsOfArea().find(({ id }) => String(id) === this.subdivisionTarget.value)
    const withData = picked ? picked.beds : []
    // 0 is a studio.
    const all = Array.from({ length: this.maxBedroomsValue + 1 }, (_, index) => index)

    if (withData.length) {
      select.replaceChildren(
        this.blankOption(select),
        this.group("With season data", withData),
        this.group("Other sizes", all.filter((count) => !withData.includes(count)))
      )
    } else {
      select.replaceChildren(this.blankOption(select), ...all.map((count) => this.bedroomOption(count)))
    }
    select.value = chosen
  }

  subdivisionsOfArea() {
    return this.treeValue[this.areaTarget.value] || []
  }

  // The "Pick ..." option the server rendered first.
  blankOption(select) {
    return select.querySelector("option[value='']")
  }

  // The server's "Not on the list" option, kept last.
  notListedOption(select) {
    return select.querySelector(`option[value='${NOT_LISTED}']`)
  }

  bedroomOption(count) {
    return new Option(count === 0 ? "Studio" : count, count)
  }

  group(label, counts) {
    const group = document.createElement("optgroup")
    group.label = label
    group.append(...counts.map((count) => this.bedroomOption(count)))
    return group
  }
}
